// file: loadBaseLayers.js
import { Vector as VectorLayer } from 'ol/layer';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import { GEOSERVER_URL, API_URL, BASE_LAYERS_API_URL, countryConfig } from './config.js';
import * as MapUtils from './mapUtils.js';
import { Style, Fill, Stroke, Circle as CircleStyle, Text } from 'ol/style';
import * as olExtent from 'ol/extent';
import { getSelectedCountryCode, getSelectedLanguageCode } from './country-language.js';
import { secureFetch } from "./securityUtil.js";
import { updateBaseLegendMessage } from './mapUtils.js';
import { getMap } from './mapInstance.js';
import HeatmapLayer from 'ol/layer/Heatmap';
import { getUserRights, hasRight } from './rightsUtil.js';
import { isLabelsEnabled } from './layerHierarchyService.js';
import { prepareNetherlandsBaseLayers } from './netherlandsBaseLayers.js';
import { prepareBelgiumBaseLayers } from './belgiumBaseLayers.js';
import { createBoundaryGeoJSON, boundaryWfsSpatialFilter } from './baseFeatureIdentity.js';
let isHeatmapMode = false;

const BASE_LAYER_RIGHT_ALIASES = {
  addresses: ['address', 'addresses', 'adres', 'adressen', 'adresse', 'adresses'],
  areas: ['area', 'areas'],
  councils: ['council', 'councils', 'municipality', 'municipalities', 'gemeente', 'gemeenten', 'commune', 'communes'],
  countries: ['country'],
  districts: ['district', 'districts', 'subregion', 'subregions', 'borough', 'boroughs'],
  neighbourhoods: ['neighbourhood', 'neighborhood', 'neighborhoods'],
  nis6: ['nis6', 'submunicipalities', 'submunicipality', 'be_deelgemeenten'],
  pc4: ['pc4', 'postcode4', 'postalcode4', 'codepostal4'],
  pc5: ['pc5', 'postcode5', 'postalcode5', 'codepostal5'],
  pc6: ['pc6', 'postcode6', 'postalcode6', 'codepostal6'],
  regions: ['region']
};


export function toggleHeatmapMode() {
  isHeatmapMode = !isHeatmapMode;

  loadDynamicBaseLayers(getMap());
}

function extractHeatmapColors(styleJson) {
  try {
    if (!styleJson) return ['#0000FF', '#FF0000']; // fallback

    const parsed = JSON.parse(styleJson);

    // Try fill color first
    if (parsed.fill && parsed.fill.color) {
      return generateGradient(parsed.fill.color);
    }

    // Try stroke color
    if (parsed.stroke && parsed.stroke.color) {
      return generateGradient(parsed.stroke.color);
    }

    // Try point color
    if (parsed.image && parsed.image.fill?.color) {
      return generateGradient(parsed.image.fill.color);
    }

    return ['#0000FF', '#FF0000'];

  } catch (e) {
    console.warn("Failed to extract heatmap color", e);
    return ['#0000FF', '#FF0000'];
  }
}

function extractRGB(color) {
  // handle rgba or rgb
  if (color.startsWith('rgb')) {
    const nums = color.match(/\d+/g).map(Number);
    return { r: nums[0], g: nums[1], b: nums[2] };
  }

  // handle hex (#RRGGBB)
  if (color.startsWith('#')) {
    const bigint = parseInt(color.slice(1), 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    };
  }

  // fallback (blue)
  return { r: 0, g: 0, b: 255 };
}

function generateGradient(baseColor) {
  const rgb = extractRGB(baseColor);

  return [
    'rgba(0,0,0,0)',                        // transparent
    `rgba(${rgb.r},${rgb.g},${rgb.b},0.3)`,
    `rgba(${rgb.r},${rgb.g},${rgb.b},0.6)`,
    `rgba(${rgb.r},${rgb.g},${rgb.b},0.9)`,
    `rgba(${rgb.r},${rgb.g},${rgb.b},1)`
  ];
}

// expose to HTML
window.toggleHeatmapMode = toggleHeatmapMode;


export function parseStyleFromJSON(styleJson) {
  try {
    if (!styleJson) return getDefaultStyle();

    const parsed = JSON.parse(styleJson);

    // Polygon-style
    if (parsed.fill || parsed.stroke) {
      return new Style({
        fill: parsed.fill ? new Fill({ color: parsed.fill.color }) : undefined,
        stroke: parsed.stroke ? new Stroke({ color: parsed.stroke.color, width: parsed.stroke.width }) : undefined
      });
    }

    // Point-style (circle)
    if (parsed.image) {
      return new Style({
        image: new CircleStyle({
          radius: parsed.image.radius || 5,
          fill: parsed.image.fill ? new Fill({ color: parsed.image.fill.color }) : undefined,
          stroke: parsed.image.stroke
            ? new Stroke({ color: parsed.image.stroke.color, width: parsed.image.stroke.width })
            : undefined
        })
      });
    }

    return getDefaultStyle();
  } catch (err) {
    console.warn('Failed to parse style JSON:', err);
    return getDefaultStyle();
  }
}

function isClosedPolygonGeometry(feature) {
  const type = feature?.getGeometry?.()?.getType?.();
  return type === 'Polygon' || type === 'MultiPolygon';
}

function getFeatureValue(feature, candidates) {
  for (const key of candidates) {
    const value = feature?.get?.(key);
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }

  const properties = feature?.getProperties?.() || {};
  const normalizedCandidates = candidates.map(key => String(key).toLowerCase());
  const match = Object.keys(properties).find(key => normalizedCandidates.includes(key.toLowerCase()));
  const value = match ? properties[match] : null;

  return value !== undefined && value !== null && String(value).trim() !== '' ? value : null;
}

function getFeatureValueByKeyPattern(feature, patterns) {
  const properties = feature?.getProperties?.() || {};

  for (const [key, value] of Object.entries(properties)) {
    if (key === 'geometry' || value === undefined || value === null || String(value).trim() === '') {
      continue;
    }

    const normalizedKey = String(key).toLowerCase().replace(/[^a-z0-9]/g, '');
    if (
      normalizedKey === 'id'
      || normalizedKey === 'fid'
      || normalizedKey === 'objectid'
      || normalizedKey.endsWith('sourceid')
    ) {
      continue;
    }

    if (patterns.some(pattern => pattern.test(normalizedKey))) {
      return value;
    }
  }

  return null;
}

function getAddressCount(feature) {
  const value = Number(getFeatureValue(feature, [
    'u108212Aantal_VBO_BAG',
    'number_of_addresses',
    'addressCount',
    'addresses',
    'count',
    'aantal_adressen',
    'aantal_vbo'
  ]));

  return Number.isFinite(value) ? value : null;
}

function getSelectedLabelLanguageSuffix() {
  const option = document.getElementById('language-select')?.selectedOptions?.[0];
  const language = [option?.dataset?.abbreviation, option?.dataset?.name, option?.textContent]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/\b(fr|french|fran[cç]ais)\b/.test(language)) return 'fr';
  if (/\b(de|ger|german|deutsch)\b/.test(language)) return 'de';
  return 'nl';
}

function getLanguageAwareAdminName(feature) {
  const selectedLanguage = getSelectedLabelLanguageSuffix();
  const languageOrder = [selectedLanguage, ...['nl', 'fr', 'de'].filter(code => code !== selectedLanguage)];
  const keysByLanguage = {
    nl: ['u108267Admin_NL', 'Admin_NL', 'admin_nl', 'naam_nl', 'name_nl'],
    fr: ['u108266Admin_FR', 'Admin_FR', 'admin_fr', 'naam_fr', 'name_fr'],
    de: ['u108265Admin_GER', 'Admin_GER', 'Admin_DE', 'admin_ger', 'admin_de', 'naam_de', 'name_de']
  };
  const patternsByLanguage = {
    nl: [/admin(nl|naam)$/, /(name|naam)nl$/],
    fr: [/adminfr$/, /(name|naam)fr$/],
    de: [/admin(ger|de)$/, /(name|naam)(ger|de)$/]
  };

  const directValue = getFeatureValue(
    feature,
    languageOrder.flatMap(language => keysByLanguage[language])
  );
  if (directValue !== null) return directValue;

  for (const language of languageOrder) {
    const patternValue = getFeatureValueByKeyPattern(feature, patternsByLanguage[language]);
    if (patternValue !== null) return patternValue;
  }

  return null;
}

function getPolygonName(feature, layerInfo = {}) {
  return getLanguageAwareAdminName(feature) || getFeatureValue(feature, [
    'name',
    'naam',
    'Name',
    'NAME',
    'Admin_naam',
    'Admin_name',
    'admin_naam',
    'admin_name',
    'u108213Admin_naam',
    'u108213Admin_name',
    'description',
    'omschrijving',
    'display_name',
    'country',
    'country_name',
    'countryName',
    'land',
    'pays',
    'region',
    'region_name',
    'regionName',
    'regio',
    'subregion',
    'sub_region',
    'subregion_name',
    'province',
    'province_name',
    'provincie',
    'provincie_naam',
    'municipality',
    'municipality_name',
    'gemeente',
    'gemeente_naam',
    'council',
    'council_name',
    'neighbourhood',
    'neighbourhood_name',
    'neighborhood',
    'neighborhood_name',
    'buurt',
    'wijk',
    's_name',
    's_naam',
    's_label',
    's_title',
    's_description',
    's_omschrijving',
    'displayName',
    'label',
    'postcode',
    'postcode6_BAG',
    'pc6',
    'pc5',
    'pc4',
    'u104986pc6_cbs',
    'u104987pc4_cbs'
  ]) || getFeatureValueByKeyPattern(feature, [
    /admin.*(naam|name)/,
    /(^|.*)(name|naam|label|title|description|omschrijving)$/,
    /(country|region|regio|subregion|province|provincie|municipality|gemeente|council|neighbourhood|neighborhood|buurt|wijk)/
  ]) || '';
}

function getLayerAreaLabel(layerInfo = {}) {
  const title = String(layerInfo.title || layerInfo.name || '').trim();
  if (title) return title;

  const typeName = String(layerInfo.typeName || '').split(':').pop()?.trim() || '';
  return typeName.replace(/[_-]+/g, ' ');
}

function compactLabelRows(rows) {
  const seen = new Set();

  return rows
    .map(row => String(row || '').trim())
    .filter(Boolean)
    .filter(row => {
      const key = row.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function getBelgiumPostalLabel(feature, layerInfo = {}) {
  const title = String(layerInfo.title || '').replace(/[^a-z0-9]/gi, '').toUpperCase();
  const typeName = String(layerInfo.typeName || '').toUpperCase();

  if (title === 'NIS6' || typeName.endsWith(':U1014136VIEWGROUPADRESTYPE1') || typeName === 'GEOLINQ:BE_DEELGEMEENTEN') {
    return getFeatureValue(feature, ['nis6', 'cd_sub_munty']);
  }

  if (
    title === 'POSTALCODE'
    || title === 'POSTALCODES'
    || typeName.endsWith(':U1014134VIEWGROUPADRESTYPE1')
    || typeName === 'GEOLINQ:BE_4PP'
  ) {
    return getFeatureValue(feature, ['nouveau_po', 'bd_code', 'postcode', 'postal_code']);
  }

  return null;
}

function getPolygonLabelText(feature, layerInfo = {}) {
  if (!isLabelsEnabled()) return '';

  const name = String(
    getBelgiumPostalLabel(feature, layerInfo)
    || getPolygonName(feature, layerInfo)
    || ''
  ).trim();

  return compactLabelRows([name]).join('\n');
}

function createPolygonTextStyle(labelText) {
  if (!labelText) return undefined;

  return new Text({
    text: labelText,
    overflow: false,
    placement: 'point',
    font: '600 12px Arial, sans-serif',
    fill: new Fill({ color: '#111827' }),
    stroke: new Stroke({ color: 'rgba(255,255,255,0.9)', width: 3 }),
    textAlign: 'center',
    textBaseline: 'middle'
  });
}

function createUniformPolygonStyle(backendStyle, labelText = '') {
  const fillColor = backendStyle?.getFill?.()?.getColor?.();
  const stroke = backendStyle?.getStroke?.();

  return new Style({
    fill: fillColor ? new Fill({ color: fillColor }) : undefined,
    stroke: stroke
      ? new Stroke({
          color: stroke.getColor(),
          width: stroke.getWidth(),
          lineCap: stroke.getLineCap(),
          lineJoin: stroke.getLineJoin(),
          lineDash: stroke.getLineDash(),
          lineDashOffset: stroke.getLineDashOffset(),
          miterLimit: stroke.getMiterLimit()
        })
      : undefined,
    text: createPolygonTextStyle(labelText)
  });
}

function createBaseLayerStyleFunction(styleJson, layerInfo = {}) {
  const backendStyle = parseStyleFromJSON(styleJson);
  // Legacy API metadata may only define a point symbol for a polygon source.
  const polygonBackendStyle = backendStyle?.getFill?.() || backendStyle?.getStroke?.()
    ? backendStyle : getDefaultStyle();
  const polygonStyle = createUniformPolygonStyle(polygonBackendStyle);
  const labelStyles = new WeakMap();
  const pointStyle = backendStyle?.getImage?.()
    ? backendStyle
    : getDefaultStyle();

  return feature => {
    if (!feature) {
      return pointStyle;
    }

    if (isClosedPolygonGeometry(feature)) {
      const labelText = getPolygonLabelText(feature, layerInfo);
      if (!labelText) return polygonStyle;
      let cached = labelStyles.get(feature);
      if (!cached || cached.labelText !== labelText) {
        cached = { labelText, style: createUniformPolygonStyle(polygonBackendStyle, labelText) };
        labelStyles.set(feature, cached);
      }
      return cached.style;
    }

    return pointStyle;
  };
}

const addressPointStyle = new Style({
  image: new CircleStyle({
    radius: 2,
    fill: new Fill({ color: '#ff0000' })
  })
});

function createAddressLayerStyleFunction(styleJson, layerInfo = {}) {
  const baseStyleFunction = createBaseLayerStyleFunction(styleJson, layerInfo);

  return feature => {
    if (!feature) {
      return addressPointStyle;
    }

    if (isClosedPolygonGeometry(feature)) {
      return baseStyleFunction(feature);
    }

    return addressPointStyle;
  };
}

function getDefaultStyle() {
  return new Style({
    fill: new Fill({ color: 'rgba(0, 0, 255, 0.1)' }),
    stroke: new Stroke({ color: '#0000FF', width: 1 }),
    image: new CircleStyle({ radius: 5, fill: new Fill({ color: '#0000FF' }) })
  });
}

function getChoroplethStyle(feature) {
  const val = feature.get('u108212Aantal_VBO_BAG') || 0;

  let color = '#00FF00'; // low

  if (val > 100) color = '#FFFF00';
  if (val > 300) color = '#FFA500';
  if (val > 600) color = '#FF0000';

  return new Style({
    fill: new Fill({
      color: color + '80' // transparency
    }),
    stroke: new Stroke({
      color: '#333',
      width: 1
    })
  });
}

function generatePointsInPolygon(feature, count = 5) {
  const geom = feature.getGeometry();
  const extent = geom.getExtent();
  const points = [];

  for (let i = 0; i < count; i++) {
    const x = extent[0] + Math.random() * (extent[2] - extent[0]);
    const y = extent[1] + Math.random() * (extent[3] - extent[1]);

    const point = new GeoJSON().readFeature({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [x, y]
      },
      properties: feature.getProperties()
    }, {
      dataProjection: 'EPSG:3857',
      featureProjection: 'EPSG:3857'
    });

    points.push(point);
  }

  return points;
}

function getLayerRightCandidates(layerInfo) {
  const candidates = [
    layerInfo.title,
    layerInfo.typeName,
    layerInfo.typeName?.split(':').pop(),
    layerInfo.title ? `layers.base.${layerInfo.title}` : null,
    layerInfo.typeName ? `layers.base.${layerInfo.typeName}` : null,
    layerInfo.typeName ? `layers.base.${layerInfo.typeName.split(':').pop()}` : null
  ];

  return [...new Set(
    candidates
      .map(value => typeof value === 'string' ? value.trim() : '')
      .filter(Boolean)
  )];
}

function normalizeLayerRight(value) {
  if (typeof value !== 'string') return '';

  return value
    .trim()
    .toLowerCase()
    .replace(/^layers\.base\./, '')
    .replace(/^geolinq:/, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function getRightKeyVariants(value) {
  const normalized = normalizeLayerRight(value);
  if (!normalized) return [];

  const variants = new Set([normalized]);

  if (normalized.endsWith('ies')) {
    variants.add(`${normalized.slice(0, -3)}y`);
  }

  if (normalized.endsWith('es')) {
    variants.add(normalized.slice(0, -2));
  }

  if (normalized.endsWith('s')) {
    variants.add(normalized.slice(0, -1));
  }

  Object.entries(BASE_LAYER_RIGHT_ALIASES).forEach(([rightKey, aliases]) => {
    if (normalized === rightKey || aliases.includes(normalized)) {
      variants.add(rightKey);
      aliases.forEach(alias => variants.add(alias));
    }
  });

  return [...variants];
}

function getSemanticBaseLayerRole(layerInfo) {
  const candidates = getLayerRightCandidates(layerInfo)
    .map(normalizeLayerRight)
    .filter(Boolean);

  const title = normalizeLayerRight(layerInfo.title);
  const typeName = normalizeLayerRight(layerInfo.typeName);
  if (title === 'nis6' || typeName === 'u1014136viewgroupadrestype1' || typeName === 'be_deelgemeenten') {
    return 'nis6';
  }
  if (
    title === 'postalcode'
    || title === 'postalcodes'
    || typeName === 'u1014134viewgroupadrestype1'
    || typeName === 'be_4pp'
  ) {
    return 'pc6';
  }

  const semanticRoles = ['addresses', 'pc4', 'pc5', 'pc6'];

  for (const role of semanticRoles) {
    const aliases = BASE_LAYER_RIGHT_ALIASES[role] || [];
    const roleKey = normalizeLayerRight(role);

    if (candidates.some(candidate =>
      candidate === roleKey || aliases.some(alias => candidate === normalizeLayerRight(alias))
    )) {
      return role === 'addresses' ? 'address' : role;
    }
  }

  return null;
}

function hasBaseLayerRight(layerInfo) {
  const countryCode = String(getSelectedCountryCode() || '').trim().toLowerCase();
  const sourceName = String(layerInfo.typeName || '').trim().toLowerCase().split(':').pop();
  if (['1', 'nl'].includes(countryCode) && sourceName === 'nl_gemeenten') {
    return hasRight('Municipalities') || hasRight('Municipality');
  }
  if (['1', 'nl'].includes(countryCode) && sourceName === 'nl_wijken') {
    return hasRight('Districts') || hasRight('District');
  }
  if (['4', 'be'].includes(countryCode) && sourceName === 'be_municipalities') {
    return hasRight('Municipalities') || hasRight('Municipality');
  }
  if (['4', 'be'].includes(countryCode) && sourceName === 'be_deelgemeenten') {
    return hasRight('SubMunicipalities') || hasRight('SubMunicipality');
  }
  const candidates = getLayerRightCandidates(layerInfo);
  if (candidates.some(candidate => hasRight(candidate))) return true;

  const userRightKeys = new Set(getUserRights().flatMap(getRightKeyVariants));

  return candidates.some(candidate =>
    getRightKeyVariants(candidate).some(candidateKey => userRightKeys.has(candidateKey))
  );
}

function isBaseLayerVisibleByDefault(layerInfo) {
  return hasBaseLayerRight(layerInfo) && layerInfo.visible === true;
}

function applyLayerRangeOptions(options, layerInfo) {
  const range = getEffectiveLayerVisibilityRange(layerInfo);
  options.normalizedVisibilityRange = range;
  if (getSemanticBaseLayerRole(layerInfo) === 'address') {
    const countryCode = String(getSelectedCountryCode() || '').trim();
    if (countryCode === '1') options.layerInfoTargetZoom = 16;
    if (countryCode === '4') options.layerInfoTargetZoom = 15.1;
  }

  if (range.mode === 'zoom') {
    // OpenLayers excludes minZoom; the application's configured lower bound
    // is inclusive, including the exact zoom used by the Layer Info shortcut.
    options.minZoom = range.minZoom - 1e-9;
    options.maxZoom = range.maxZoom;
  } else {
    options.minResolution = range.minResolution;
    options.maxResolution = range.maxResolution;
  }

  return range;
}

function getEffectiveLayerVisibilityRange(layerInfo, semanticRole = getSemanticBaseLayerRole(layerInfo)) {
  const range = MapUtils.normalizeLayerVisibilityRange(layerInfo.minResolution, layerInfo.maxResolution);

  if (semanticRole !== 'address') return range;

  const countryCode = String(getSelectedCountryCode() || '').trim();
  if (countryCode !== '4' || range.mode !== 'zoom') return range;

  const belgiumAddressMinZoom = MapUtils.zoomFromScaleDenominator(6000);
  if (!Number.isFinite(belgiumAddressMinZoom)) return range;

  return {
    ...range,
    minZoom: Math.min(range.minZoom, belgiumAddressMinZoom)
  };
}


function getBaseLayerZIndex(layerInfo, semanticRole) {
  if (semanticRole === 'address') return 10000;
  const order = Number(layerInfo.displayOrder);
  // Hierarchy order starts at zero for the topmost layer. Basemap tiles use
  // z-index 2, so never reuse displayOrder directly as the drawing z-index.
  return Number.isFinite(order) ? Math.max(3, 1000 - order) : 3;
}

export async function loadDynamicBaseLayers(map) {
  try {

    if (!map) {
      //console.warn("loadDynamicBaseLayers skipped: base_map missing");
      return;
    }

    //get values dynamically from dropdowns
    const country = getSelectedCountryCode();
    const language = getSelectedLanguageCode();

    if (!country || !language) {
      //console.warn("Country or language not selected yet. Skipping base layer load.");
      return;
    }

    const url = `${BASE_LAYERS_API_URL}base-layers/raw?country=${country}&language=${language}`;

    const response = await secureFetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Server returned ${response.status}`);
    const layersData = prepareBelgiumBaseLayers(prepareNetherlandsBaseLayers(await response.json(), country), country);

    //Remove only existing base layers (don't touch overlays or own data)
    //const baseLayers = map.getLayers().getArray().filter(l => l.get('isBase'));
    //baseLayers.forEach(layer => map.getLayers().remove(layer));
    window.base_map.getLayers().clear();

    layersData.forEach(layerInfo => {
      
      if (!layerInfo.title) return;



      // Use the replacement GeoServer source for legacy API metadata.
      const originalTypeName = String(layerInfo.typeName || '').trim().toLowerCase();
      const addressCountry = {
        'geolinq:u101093viewvbodienstversietype': 'NL',
        'geolinq:nl_full_address': 'NL',
        'geolinq:u101234be_adres': 'BE',
        'geolinq:be_full_address': 'BE'
      }[originalTypeName];
      if (addressCountry) {
        layerInfo = { ...layerInfo, typeName: countryConfig[addressCountry].layer };
      }
      const replacementTypeName = {
        'geolinq:u1014134viewgroupadrestype1': 'geolinq:be_4pp',
        'geolinq:u101393viewgroupadrestype1': 'geolinq:be_country',
        'geolinq:u101403viewgroupadrestype1': 'geolinq:be_regions',
        'geolinq:u101405viewgroupadrestype1': 'geolinq:be_provinces',
        'geolinq:u101409viewgroupadrestype1': 'geolinq:be_arrondissements',
        'geolinq:u101411viewgroupadrestype1': 'geolinq:be_municipalities',
        'geolinq:u101413viewgroupadrestype1': 'geolinq:be_statistical_sectors',
        'geolinq:u1014136viewgroupadrestype1': 'geolinq:be_deelgemeenten',
        'geolinq:u101389viewgroupvbotype1': 'geolinq:nl_landgebied',
        'geolinq:u101379viewgroupvbotype1': 'geolinq:nl_provinciegebied',
        'geolinq:u101377viewgroupvbotype1': 'geolinq:nl_gemeenten',
        'geolinq:u101375viewgroupvbotype1': 'geolinq:nl_buurten',
        'geolinq:u101365viewgroupvbotype1': 'geolinq:nl_4pp',
        'geolinq:u101373viewgroupvbotype1': 'geolinq:nl_6pp'
      }[originalTypeName];
      if (replacementTypeName) {
        layerInfo = { ...layerInfo, typeName: replacementTypeName };
      }
      if (originalTypeName === 'geolinq:nl_wijken'
        && /^sub\s*regions?$/i.test(String(layerInfo.title || '').trim())) {
        layerInfo = { ...layerInfo, typeName: 'geolinq:nl_gemeenten' };
      }
      if (String(layerInfo.typeName || '').toLowerCase() === 'geolinq:nl_gemeenten') {
        layerInfo = { ...layerInfo, title: 'Municipalities' };
      }
      if (String(layerInfo.typeName || '').toLowerCase() === 'geolinq:be_country') {
        layerInfo = { ...layerInfo, minResolution: 6, maxResolution: 0 };
      }
      if (String(layerInfo.typeName || '').toLowerCase() === 'geolinq:be_regions') {
        layerInfo = { ...layerInfo, minResolution: 8, maxResolution: 6 };
      }
      if (String(layerInfo.typeName || '').toLowerCase() === 'geolinq:be_arrondissements') {
        layerInfo = { ...layerInfo, title: 'Borough', minResolution: 11, maxResolution: 9 };
      }
      if (String(layerInfo.typeName || '').toLowerCase() === 'geolinq:be_provinces') {
        layerInfo = { ...layerInfo, minResolution: 9, maxResolution: 8 };
      }
      if (String(layerInfo.typeName || '').toLowerCase() === 'geolinq:be_municipalities') {
        layerInfo = { ...layerInfo, title: 'Municipalities', minResolution: 12, maxResolution: 11 };
      }
      if (String(layerInfo.typeName || '').toLowerCase() === 'geolinq:be_deelgemeenten') {
        layerInfo = { ...layerInfo, title: 'Sub Municipalities', minResolution: 13, maxResolution: 12 };
      }
      if (String(layerInfo.typeName || '').toLowerCase() === 'geolinq:be_4pp') {
        layerInfo = { ...layerInfo, minResolution: 15, maxResolution: 13 };
      }

      const hasLayerRight = hasBaseLayerRight(layerInfo);
      const visibleByDefault = isBaseLayerVisibleByDefault(layerInfo);
      const featureFormat = createBoundaryGeoJSON({
          // GeoServer may also return properties.geometry; keep it from
          // overwriting the OpenLayers geometry used for rendering.
          geometryName: '__mapGeometry',
          dataProjection: 'EPSG:3857',
          featureProjection: 'EPSG:3857'
        }, layerInfo.typeName);
      const source = new VectorSource({
        overlaps: false,
        format: featureFormat,
        url: extent =>
          `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature&typename=${layerInfo.typeName}&outputFormat=application/json&srsname=EPSG:3857&${boundaryWfsSpatialFilter(layerInfo.typeName, extent)}`,
        strategy: bboxStrategy
      });

      source.on('addfeature', e => {
        const feature = e.feature;
        const customId = feature.get('s_source_id');
        if (customId == null) return;

        const featureId = String(customId);
        const existingFeature = source.getFeatureById(featureId);
        if (existingFeature && existingFeature !== feature) {
          source.removeFeature(feature);
          return;
        }

        feature.setId(featureId);
      });



      let maxValue = 1;
      const heatmapSource = new VectorSource();

      source.on('addfeature', (e) => {
        if (!isHeatmapMode) return;

        const feature = e.feature;
        const geom = feature.getGeometry();

        const val = feature.get('u108212Aantal_VBO_BAG') || 0;

        // FIX: track max value
        if (val > maxValue) maxValue = val;

        if (geom.getType() === 'Polygon') {
          const extent = geom.getExtent();

          for (let i = 0; i < 15; i++) {
            const x = extent[0] + Math.random() * (extent[2] - extent[0]);
            const y = extent[1] + Math.random() * (extent[3] - extent[1]);

            const pointFeature = new GeoJSON().readFeature({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [x, y]
              },
              properties: feature.getProperties()
            }, {
              dataProjection: 'EPSG:3857',
              featureProjection: 'EPSG:3857'
            });

            heatmapSource.addFeature(pointFeature);
          }

        } else {
          heatmapSource.addFeature(feature.clone());
        }
      });

      let layer;
      const semanticRole = getSemanticBaseLayerRole(layerInfo);

      if (isHeatmapMode) {

        const gradient = extractHeatmapColors(layerInfo.style);
        const heatmapOptions = {
          source: heatmapSource,
          title: layerInfo.title,
          visible: visibleByDefault,

            weight: (feature) => {
              const val = feature.get('u108212Aantal_VBO_BAG') || 0;

              if (val < 20) return 0;

              return Math.pow(val / maxValue, 0.5);
            },

          gradient: [
            'rgba(0,0,0,0)',
            'rgba(0,0,255,0.4)',
            'rgba(0,255,255,0.6)',
            'rgba(255,255,0,0.8)',
            'rgba(255,128,0,0.9)',
            'rgba(255,0,0,1)'
          ],

          blur: 30,
          radius: 40
        };

        applyLayerRangeOptions(heatmapOptions, layerInfo);

        // HEATMAP MODE
        layer = new HeatmapLayer(heatmapOptions);

        source.once('change', () => {
          if (source.getState() === 'ready') {

            // trigger heatmap refresh
            setTimeout(() => {
              layer.changed();
            }, 300);
          }
        });

            // force redraw after load
          source.on('addfeature', (e) => {
            const feature = e.feature;
            const geom = feature.getGeometry();
          });

      } else {
        const backendStyle = parseStyleFromJSON(layerInfo.style);
        const layerOptions = {
          title: layerInfo.title,
          source,
          visible: visibleByDefault,
          style: semanticRole === 'address'
            ? createAddressLayerStyleFunction(layerInfo.style, layerInfo)
            : createBaseLayerStyleFunction(layerInfo.style, layerInfo),
          renderMode: 'vector',
          updateWhileAnimating: false,
          updateWhileInteracting: false
        };

        applyLayerRangeOptions(layerOptions, layerInfo);

        // NORMAL MODE
        layer = new VectorLayer(layerOptions);
      }

      // common props
      layer.set('isBase', true);
      layer.set('title', layerInfo.title);
      layer.set('typeName', layerInfo.typeName);
      layer.set('backendStyle', layerInfo.style);
      // `visible` controls only the initial checkbox state. A layer that starts
      // switched off is still available and may be enabled by the user.
      layer.set('backendVisible', true);
      layer.set('backendDefaultVisible', layerInfo.visible === true);
      layer.set('displayOrder', layerInfo.displayOrder);
      layer.set('configuredMinResolution', layerInfo.minResolution);
      layer.set('configuredMaxResolution', layerInfo.maxResolution);
      layer.set('normalizedVisibilityRange', getEffectiveLayerVisibilityRange(layerInfo, semanticRole));
      layer.set('wfsSource', source);
      layer.set('disabledByRights', !hasLayerRight);
      layer.set('addressAidsRole', semanticRole);
      layer.set('semanticRole', semanticRole);

      layer.setZIndex(getBaseLayerZIndex(layerInfo, semanticRole));

      source.on('featuresloadend', () => layer.changed());
      source.on('addfeature', () => layer.changed());

      window.base_map.getLayers().push(layer);

      
      setTimeout(() => {
        const realMap = getMap();
        if (realMap) {
          updateBaseLegendMessage(window.base_map);
        }
      }, 0);
      
    });

    //window.base_map = map;

   //Trigger update only when layers exist
    if (map.getLayers().getLength() > 0) 
    {
      document.dispatchEvent(new CustomEvent('base-layers-updated'));
    } 
    else 
    {
      console.warn("No base layers loaded — skipping panel update");
    }

    fitBaseLayers(getMap())

  } catch (err) {
    console.error('Error loading dynamic base layers:', err);
  }
}


/*+++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 Fit all visible base layers on the map
 +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
export function fitBaseLayers(map) {

    if (!map || typeof map.getView !== 'function') {
    console.error('fitBaseLayers called with invalid map:', map);
    return;
  }

  const allLayers = MapUtils.getAllLayers(map); // flatten all layers
  let allExtents = [];

  allLayers.forEach(layer => {
    
    if (layer.get('isBase') && layer.getVisible()) {
      
      const source = layer.getSource?.();
      if (source) {
        const features = source.getFeatures();
        if (features.length > 0) {
          allExtents.push(source.getExtent());
        } 
      }
    }
  });

  if (allExtents.length === 0) return;

  if (allExtents.length > 0) {
    const combined = allExtents.reduce((acc, ext) => {
      return olExtent.extend(acc, ext);
    }, olExtent.createEmpty());

    map.getView().fit(combined, { duration: 500, padding: [50, 50, 50, 50] });
  } else {
    return;
  }
}

export async function fitCountry(map, countryCode) {
  try {
    if (!countryCode) {
      console.warn("fitCountry: No country code provided.");
      return;
    }


    // Replace with your GeoServer layer name and filter field
    const layerName = 'geolinq:countries';
    const cqlFilter = `code='${countryCode}'`; // or ISO_CODE / COUNTRY_ID, etc.

    const wfsUrl = `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature&typename=${layerName}&outputFormat=application/json&CQL_FILTER=${encodeURIComponent(cqlFilter)}&srsname=EPSG:3857`;

    const response = await fetch(wfsUrl);
    if (!response.ok) throw new Error(`GeoServer returned ${response.status}`);

    const geojson = await response.json();
    const features = new GeoJSON().readFeatures(geojson, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });

    if (!features || features.length === 0) {
      console.warn("fitCountry: No country geometry found.");
      return;
    }

    // Compute extent
    const extent = olExtent.createEmpty();
    features.forEach(f => olExtent.extend(extent, f.getGeometry().getExtent()));

    // Fit view
    map.getView().fit(extent, { duration: 700, padding: [50, 50, 50, 50] });

    // Optional: add temporary highlight
    const highlightLayer = new VectorLayer({
      source: new VectorSource({ features }),
      style: new Style({
        stroke: new Stroke({ color: '#FF8800', width: 3 }),
        fill: new Fill({ color: 'rgba(255,136,0,0.2)' })
      }),
    });

    // Remove previous highlight if any
    const old = map.getLayers().getArray().find(l => l.get('isCountryHighlight'));
    if (old) map.removeLayer(old);

    highlightLayer.set('isCountryHighlight', true);
    map.addLayer(highlightLayer);

    // Auto-remove highlight after 3 seconds
    setTimeout(() => map.removeLayer(highlightLayer), 3000);

  } catch (err) {
    console.error("fitCountry error:", err);
  }
}

//document.dispatchEvent(new CustomEvent('base-layers-updated'));


